import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUnlock, FiLock, FiRefreshCw, FiPlus, FiMinus, FiAlertTriangle,
  FiPrinter, FiDollarSign, FiEdit2, FiSettings,
  FiShoppingBag, FiCheckCircle, FiXCircle, FiClock, FiArrowLeft,
  FiFileText, FiPackage,
} from 'react-icons/fi';
import api, {
  getWarehouses, getAllOpenCashRegisters,
  getCashDesks, createCashDesk, updateCashDesk, toggleCashDeskActive,
  getCashRegisterCurrent, getCashRegisterLatest, reopenCashRegister,
  openCashRegister, getCashRegisterSummary, closeCashRegister,
  getCashRegisterMovements, addCashRegisterMovement,
  getCashRegisterHistory,
} from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/AperturaCajas.css';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDatetime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('es-PE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: true, timeZone: 'America/Lima',
  });
};

const PM_COLORS = {
  'EFECTIVO':               { bg: '#dcfce7', color: '#166534', dot: '#16a34a' },
  'CRÉDITO':                { bg: '#ffedd5', color: '#9a3412', dot: '#ea580c' },
  'TARJETA CRED. O DEBITO': { bg: '#dbeafe', color: '#1e40af', dot: '#2563eb' },
  'TRANSFERENCIA BANCARIA': { bg: '#e0e7ff', color: '#3730a3', dot: '#4338ca' },
  'PLIN':                   { bg: '#cffafe', color: '#0e7490', dot: '#0891b2' },
  'YAPE':                   { bg: '#f3e8ff', color: '#6b21a8', dot: '#7c3aed' },
};

const getPmStyle = (name) =>
  PM_COLORS[name] || { bg: '#f1f5f9', color: '#334155', dot: '#64748b' };

// ── Denominaciones peruanas ───────────────────────────────────────────────────
const BILLETES = [
  { value: 200, label: '200', color: '#1d4ed8', bg: '#dbeafe' },
  { value: 100, label: '100', color: '#065f46', bg: '#d1fae5' },
  { value: 50,  label: '50',  color: '#7c3aed', bg: '#ede9fe' },
  { value: 20,  label: '20',  color: '#b45309', bg: '#fef3c7' },
  { value: 10,  label: '10',  color: '#9f1239', bg: '#ffe4e6' },
];
const MONEDAS = [
  { value: 5,    label: '5',    color: '#374151', bg: '#e5e7eb' },
  { value: 2,    label: '2',    color: '#374151', bg: '#e5e7eb' },
  { value: 1,    label: '1',    color: '#374151', bg: '#f3f4f6' },
  { value: 0.50, label: '0.50', color: '#374151', bg: '#f9fafb' },
  { value: 0.20, label: '0.20', color: '#374151', bg: '#f9fafb' },
  { value: 0.10, label: '0.10', color: '#374151', bg: '#f9fafb' },
];
const ALL_DENOMS = [...BILLETES, ...MONEDAS];

// ── Modal: Cuadre de Cierre ────────────────────────────────────────────────────
function ModalCuadre({ onConfirm, onCancel, totalEfectivo }) {
  const [denoms, setDenoms] = useState({});

  const saldo = ALL_DENOMS.reduce((sum, d) => {
    return sum + d.value * parseInt(denoms[d.value] || 0, 10);
  }, 0);

  const diferencia = saldo - totalEfectivo;

  const setQty = (val, qty) => {
    const n = parseInt(qty, 10);
    setDenoms(prev => ({ ...prev, [val]: isNaN(n) || n < 0 ? 0 : n }));
  };

  return (
    <div className="caj-overlay">
      <div className="caj-modal caj-modal-cuadre" onClick={e => e.stopPropagation()}>
        <div className="caj-modal-header caj-modal-header-close">
          <FiLock size={18} />
          <span>Cuadre de Cierre</span>
        </div>

        <div className="caj-cuadre-body">
          <p className="caj-cuadre-subtitle">Ingresa las cantidades de billetes y monedas en caja</p>

          <div className="caj-denom-section">
            <div className="caj-denom-label">Billetes</div>
            <div className="caj-denom-grid">
              {BILLETES.map(d => (
                <div key={d.value} className="caj-denom-card" style={{ borderColor: d.color }}>
                  <div className="caj-denom-pill" style={{ background: d.bg, color: d.color }}>
                    S/{d.label}
                  </div>
                  <input
                    type="number" min="0"
                    className="caj-denom-input"
                    value={denoms[d.value] || ''}
                    onChange={e => setQty(d.value, e.target.value)}
                    placeholder="0"
                  />
                  {(denoms[d.value] > 0) && (
                    <div className="caj-denom-sub" style={{ color: d.color }}>
                      = S/{fmt(d.value * parseInt(denoms[d.value], 10))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="caj-denom-section">
            <div className="caj-denom-label">Monedas</div>
            <div className="caj-denom-grid caj-denom-grid-coins">
              {MONEDAS.map(d => (
                <div key={d.value} className="caj-denom-card caj-denom-coin" style={{ borderColor: '#9ca3af' }}>
                  <div className="caj-denom-coin-circle">
                    <span>S/{d.label}</span>
                  </div>
                  <input
                    type="number" min="0"
                    className="caj-denom-input"
                    value={denoms[d.value] || ''}
                    onChange={e => setQty(d.value, e.target.value)}
                    placeholder="0"
                  />
                  {(denoms[d.value] > 0) && (
                    <div className="caj-denom-sub">
                      = S/{fmt(d.value * parseInt(denoms[d.value], 10))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="caj-cuadre-totals">
            <div className="caj-cuadre-row">
              <span>Total esperado en efectivo</span>
              <span className="caj-cuadre-esperado">S/ {fmt(totalEfectivo)}</span>
            </div>
            <div className="caj-cuadre-row caj-cuadre-saldo">
              <span>SALDO EN CAJA</span>
              <span className="caj-cuadre-saldo-val">S/ {fmt(saldo)}</span>
            </div>
            <div className="caj-cuadre-row">
              <span>Diferencia</span>
              <span className={diferencia < 0 ? 'caj-neg' : diferencia > 0 ? 'caj-pos' : 'caj-zero'}>
                S/ {fmt(diferencia)}
              </span>
            </div>
          </div>
        </div>

        <div className="caj-modal-footer">
          <button className="caj-btn caj-btn-secondary" onClick={onCancel}>
            <FiXCircle size={16} /> Cancelar
          </button>
          <button className="caj-btn caj-btn-danger" onClick={() => onConfirm(saldo)}>
            <FiLock size={16} /> Confirmar Cierre
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Apertura de Caja (solo Soles) ─────────────────────────────────────
function ModalAbrirCaja({ caja, onOpen, onCancel, loading }) {
  const [openAmount, setOpenAmount] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    onOpen(caja.id, parseFloat(openAmount) || 0);
  };

  return (
    <div className="caj-overlay">
      <div className="caj-modal caj-modal-apertura" onClick={e => e.stopPropagation()}>
        <div className="caj-modal-header caj-modal-header-open">
          <FiUnlock size={18} />
          <span>Apertura de Caja</span>
        </div>
        <form onSubmit={handleSubmit} className="caj-modal-body caj-form">
          <div className="caj-warehouse-badge-row">
            <span className="caj-warehouse-badge">{caja.name}</span>
            <span className="caj-warehouse-subtag">{caja.warehouse_name}</span>
          </div>

          {/* Monto de apertura */}
          <label className="caj-label">
            Monto de Apertura (S/)
            <input
              type="number" min="0" step="0.01"
              className="caj-input caj-input-lg"
              value={openAmount}
              onChange={e => setOpenAmount(e.target.value)}
              placeholder="0.00"
              autoFocus
            />
            <span className="caj-hint">Efectivo inicial en caja. Puede ser 0.</span>
          </label>

          <div className="caj-modal-footer">
            <button type="button" className="caj-btn caj-btn-secondary" onClick={onCancel} disabled={loading}>
              <FiXCircle size={16} /> Cancelar
            </button>
            <button type="submit" className="caj-btn caj-btn-success" disabled={loading}>
              <FiUnlock size={16} /> {loading ? 'Abriendo...' : 'Abrir Caja'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal: Crear / Editar Caja (solo admin) ───────────────────────────────────
function ModalCaja({ mode, initial, warehouses, users, onSave, onCancel, loading }) {
  const [name, setName]           = useState(initial?.name || '');
  const [warehouseId, setWhId]    = useState(initial?.warehouse_id ? String(initial.warehouse_id) : '');
  const [assignedId, setAssigned] = useState(initial?.assigned_user_id ? String(initial.assigned_user_id) : '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !warehouseId) return;
    onSave({
      name: name.trim(),
      warehouse_id: parseInt(warehouseId, 10),
      assigned_user_id: assignedId ? parseInt(assignedId, 10) : null,
    });
  };

  return (
    <div className="caj-overlay">
      <div className="caj-modal caj-modal-apertura" onClick={e => e.stopPropagation()}>
        <div className="caj-modal-header caj-modal-header-open">
          <FiSettings size={18} />
          <span>{mode === 'create' ? 'Nueva Caja' : 'Editar Caja'}</span>
        </div>
        <form onSubmit={handleSubmit} className="caj-modal-body caj-form">
          <label className="caj-label">
            Nombre de la Caja
            <input
              type="text"
              className="caj-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ej: Caja 1 — Mostrador"
              autoFocus
              required
            />
          </label>

          <label className="caj-label">
            Almacén
            <select className="caj-input" value={warehouseId} onChange={e => setWhId(e.target.value)} required>
              <option value="">-- Seleccionar almacén --</option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </label>

          <label className="caj-label">
            Usuario Asignado
            <select className="caj-input" value={assignedId} onChange={e => setAssigned(e.target.value)}>
              <option value="">-- Sin asignar --</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
              ))}
            </select>
            <span className="caj-hint">Solo el usuario asignado (o un administrador) puede abrir esta caja.</span>
          </label>

          <div className="caj-modal-footer">
            <button type="button" className="caj-btn caj-btn-secondary" onClick={onCancel} disabled={loading}>
              <FiXCircle size={16} /> Cancelar
            </button>
            <button type="submit" className="caj-btn caj-btn-success" disabled={loading}>
              <FiCheckCircle size={16} /> {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal: Agregar Movimiento ─────────────────────────────────────────────────
function ModalMovimiento({ type, onSave, onCancel, loading }) {
  const [amount, setAmount]     = useState('');
  const [description, setDesc]  = useState('');
  const isIncome = type === 'income';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) return;
    onSave({ type, amount: parseFloat(amount), description });
  };

  return (
    <div className="caj-overlay">
      <div className="caj-modal caj-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="caj-modal-header">
          <span style={{ color: isIncome ? '#86efac' : '#fca5a5' }}>
            {isIncome ? '+ Agregar Ingreso' : '− Agregar Egreso'}
          </span>
          <button type="button" className="caj-modal-close" onClick={onCancel}><FiXCircle size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="caj-modal-body caj-form">
          <label className="caj-label">
            Concepto
            <input
              className="caj-input"
              value={description}
              onChange={e => setDesc(e.target.value)}
              placeholder={isIncome ? 'Ej: Préstamo, cobro extra...' : 'Ej: Pago proveedor, gastos...'}
              autoFocus
            />
          </label>
          <label className="caj-label">
            Monto (S/) *
            <input
              type="number" min="0.01" step="0.01"
              className="caj-input"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </label>
          <div className="caj-modal-footer">
            <button type="button" className="caj-btn caj-btn-secondary" onClick={onCancel} disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className={`caj-btn ${isIncome ? 'caj-btn-success' : 'caj-btn-danger'}`} disabled={loading}>
              {loading ? 'Guardando...' : 'Confirmar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Panel unificado de Movimientos de Caja (ingreso + egreso, estilo FERRETERIA) ──
function PanelMovimientos({ registerId, isPastDay }) {
  const [movs, setMovs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState(null); // 'income' | 'expense' | null
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState('');

  const loadMovs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCashRegisterMovements(registerId);
      setMovs(res.data.data || []);
    } finally {
      setLoading(false);
    }
  }, [registerId]);

  useEffect(() => { loadMovs(); }, [loadMovs]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      await addCashRegisterMovement(registerId, data);
      setModalType(null);
      showToast(data.type === 'income' ? 'Ingreso registrado' : 'Egreso registrado');
      await loadMovs();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="caj-mov-panel">
      {isPastDay && (
        <div className="caj-pastday-banner">
          <FiAlertTriangle size={16} />
          Esta caja pertenece a un día anterior. No se pueden registrar nuevas operaciones.
        </div>
      )}
      <div className="caj-mov-header">
        <span className="caj-mov-title">Movimientos de Caja</span>
        {!isPastDay && (
          <div className="caj-mov-btns">
            <button className="caj-btn-ingreso" onClick={() => setModalType('income')}>
              <FiPlus size={11} /> Ingreso
            </button>
            <button className="caj-btn-egreso" onClick={() => setModalType('expense')}>
              <FiMinus size={11} /> Egreso
            </button>
          </div>
        )}
      </div>

      <div className="caj-mov-list">
        {loading ? (
          <div className="caj-loading">Cargando...</div>
        ) : movs.length === 0 ? (
          <div className="caj-mov-empty">Sin movimientos registrados</div>
        ) : movs.map(m => {
          const isIncome = m.movement_type === 'income';
          const isCobro  = m.reference_type === 'ar_payment';
          return (
            <div key={m.id} className="caj-mov-item">
              <span className={`caj-mov-type ${isIncome ? 'caj-mov-type-ingreso' : 'caj-mov-type-egreso'}`}>
                {isIncome ? '+' : '-'}
              </span>
              <span className="caj-mov-concepto">{m.description || (isIncome ? 'Ingreso' : 'Egreso')}</span>
              {m.category && (
                <span className={`caj-mov-badge ${isCobro ? 'caj-mov-badge-cobro' : ''}`}>
                  {isCobro ? `COBRO · ${m.category}` : m.category}
                </span>
              )}
              <span className="caj-mov-hora">{fmtDatetime(m.movement_date)}</span>
              <span className={`caj-mov-monto ${isIncome ? 'caj-mov-monto-ingreso' : 'caj-mov-monto-egreso'}`}>
                {isIncome ? '+' : '-'} S/ {fmt(m.amount)}
              </span>
            </div>
          );
        })}
      </div>

      {toast && <div className="caj-toast">{toast}</div>}

      {modalType && (
        <ModalMovimiento
          type={modalType}
          onSave={handleSave}
          onCancel={() => !saving && setModalType(null)}
          loading={saving}
        />
      )}
    </div>
  );
}

// ── Tab Inicio: resumen de caja ────────────────────────────────────────────────
function TabInicio({ summary, onCerrar, onBalanceClick, readOnly = false, registerId, isPastDay = false }) {
  if (!summary) return <div className="caj-loading">Cargando resumen...</div>;

  const { register, ventas, ventas_total, ventas_anuladas, cobros, cobros_total, ventas_por_producto, movimientos, diferencia } = summary;
  const isOpen = register.status === 'open';

  return (
    <div className="caj-inicio">
      <div className="caj-info-grid">
        <div className="caj-info-panel">
          <div className="caj-info-row">
            <span className="caj-info-label">Responsable</span>
            <span className="caj-info-val caj-info-username">
              <span className="caj-avatar-sm">{(register.username || '?')[0].toUpperCase()}</span>
              {register.username}
            </span>
          </div>
          <div className="caj-info-row">
            <span className="caj-info-label">Estado Caja</span>
            <span className={`caj-badge-estado ${isOpen ? 'caj-badge-open' : 'caj-badge-closed'}`}>
              {isOpen ? <FiUnlock size={12} /> : <FiLock size={12} />}
              {isOpen ? 'CAJA ABIERTA' : 'CAJA CERRADA'}
            </span>
          </div>
        </div>

        <div className="caj-info-panel caj-info-right">
          <div className="caj-info-row">
            <span className="caj-info-label">Almacén</span>
            <span className="caj-info-val caj-fw">{register.warehouse_name}</span>
          </div>
          <div className="caj-info-row">
            <span className="caj-info-label">Fecha Apertura</span>
            <span className="caj-info-val">{fmtDatetime(register.opened_at)}</span>
          </div>
          {!isOpen && (
            <div className="caj-info-row">
              <span className="caj-info-label">Fecha Cierre</span>
              <span className="caj-info-val">{fmtDatetime(register.closed_at)}</span>
            </div>
          )}
          <div className="caj-info-row">
            <span className="caj-info-label">Diferencia</span>
            <span className={`caj-info-diferencia ${diferencia < 0 ? 'caj-neg' : diferencia > 0 ? 'caj-pos' : 'caj-zero'}`}>
              S/ {fmt(diferencia)}
            </span>
          </div>
          {isOpen && !readOnly && (
            <div className="caj-acciones">
              <button className="caj-btn caj-btn-secondary caj-btn-sm" onClick={onBalanceClick}>
                <FiPrinter size={14} /> Balance de Cierre
              </button>
              <button className="caj-btn caj-btn-danger caj-btn-sm" onClick={onCerrar}>
                <FiLock size={14} /> Cerrar Caja
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="caj-anuladas-bar">
        <span>Ventas Anuladas</span>
        <span className={ventas_anuladas > 0 ? 'caj-neg caj-fw' : 'caj-muted'}>
          S/ {fmt(ventas_anuladas)}
        </span>
      </div>

      <div className="caj-tables-row">
        <div className="caj-section-card">
          <div className="caj-section-title">
            <FiShoppingBag size={16} /> VENTAS
          </div>
          <table className="caj-table caj-table-compact">
            <tbody>
              {ventas.map(v => {
                const style = getPmStyle(v.name);
                return (
                  <tr key={v.name}>
                    <td>
                      <span className="caj-pm-badge" style={{ background: style.bg, color: style.color }}>
                        <span className="caj-pm-dot" style={{ background: style.dot }} />
                        {v.name}
                      </span>
                    </td>
                    <td className={`caj-right caj-fw ${v.total > 0 ? '' : 'caj-muted'}`}>
                      {v.total > 0 ? fmt(v.total) : '0.00'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="caj-tfoot-ventas">
                <td className="caj-fw">TOTAL VENTAS</td>
                <td className="caj-right caj-fw caj-accent">{fmt(ventas_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="caj-section-card">
          <div className="caj-section-title">
            <FiCheckCircle size={16} /> COBRO DE CRÉDITOS
          </div>
          {cobros.length === 0 ? (
            <div className="caj-empty-inline">Sin cobros registrados</div>
          ) : (
            <table className="caj-table caj-table-compact">
              <tbody>
                {cobros.map(c => {
                  const style = getPmStyle(c.name);
                  return (
                    <tr key={c.name}>
                      <td>
                        <span className="caj-pm-badge" style={{ background: style.bg, color: style.color }}>
                          <span className="caj-pm-dot" style={{ background: style.dot }} />
                          {c.name}
                        </span>
                      </td>
                      <td className="caj-right caj-fw">{fmt(c.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="caj-tfoot-cobros">
                  <td className="caj-fw">TOTAL COBRADO</td>
                  <td className="caj-right caj-fw caj-cobros-total">S/ {fmt(cobros_total)}</td>
                </tr>
              </tfoot>
            </table>
          )}

          <div className="caj-movimientos-box">
            <div className="caj-section-title" style={{ marginTop: 20 }}>
              <FiDollarSign size={16} /> MOVIMIENTOS EN CAJA
            </div>
            <div className="caj-movs-detail">
              <div className="caj-movs-item">
                <span>Monto Apertura</span>
                <span className="caj-pos caj-fw">{fmt(movimientos.open_amount)}</span>
              </div>
              <div className="caj-movs-item">
                <span>Ventas en Efectivo</span>
                <span className="caj-accent caj-fw">{fmt(movimientos.ventas_efectivo)}</span>
              </div>
              <div className="caj-movs-item">
                <span>Cobro Créditos Efectivo</span>
                <span className="caj-accent caj-fw">{fmt(movimientos.cobro_efectivo)}</span>
              </div>
              <div className="caj-movs-item">
                <span>Ingreso Efectivo</span>
                <span className="caj-blue caj-fw">{fmt(movimientos.ingreso_efectivo)}</span>
              </div>
              <div className="caj-movs-item">
                <span>Salida Efectivo</span>
                <span className="caj-neg">{fmt(movimientos.salida_efectivo)}</span>
              </div>
              <div className="caj-movs-divider" />
              <div className="caj-movs-item caj-movs-total-row">
                <span className="caj-fw">TOTAL EFECTIVO</span>
                <span className="caj-total-efectivo">S/ {fmt(movimientos.total_efectivo)}</span>
              </div>
              <div className="caj-movs-item caj-movs-total-row">
                <span className="caj-fw">TOTAL INGRESOS</span>
                <span className="caj-blue caj-fw">S/ {fmt(movimientos.total_ingresos)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {ventas_por_producto?.length > 0 && (
        <div className="caj-section-card" style={{ marginTop: 16 }}>
          <div className="caj-section-title">
            <FiPackage size={16} /> VENTAS POR PRODUCTO
          </div>
          <table className="caj-table caj-table-compact">
            <thead>
              <tr>
                <th>Producto</th>
                <th style={{ textAlign: 'right' }}>Cantidad</th>
                <th style={{ textAlign: 'right' }}>Unidades</th>
                <th style={{ textAlign: 'right' }}>Total S/</th>
              </tr>
            </thead>
            <tbody>
              {ventas_por_producto.map(p => (
                <tr key={p.product_id}>
                  <td>{p.product_name}</td>
                  <td className="caj-right">{fmt(p.total_qty)} {p.unit_measure}</td>
                  <td className="caj-right">{p.special_condition ? `${p.total_unidades} UND` : '—'}</td>
                  <td className="caj-right caj-fw">{fmt(p.total_soles)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="caj-tfoot-ventas">
                <td className="caj-fw" colSpan={3}>TOTAL VENDIDO</td>
                <td className="caj-right caj-fw caj-accent">
                  {fmt(ventas_por_producto.reduce((s, p) => s + p.total_soles, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {registerId && <PanelMovimientos registerId={registerId} isPastDay={isPastDay} />}
    </div>
  );
}

// ── Helper: comparar fecha con hoy (local) ────────────────────────────────
function isTodayDate(dateStr) {
  if (!dateStr) return false;
  const today = new Date();
  const d = new Date(dateStr);
  return d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
}

// ── Tarjeta de caja (abierta, cerrada o día anterior) ────────────────────────
function CajaCard({ reg, caja, onVer, onAbrir, onReabrir, isClosedView = false }) {
  const isOpen       = reg ? reg.status === 'open' : false;
  const isPastDay    = isOpen && (reg.is_past_day || !isTodayDate(reg.opened_at));
  const isClosed     = reg && reg.status === 'closed';
  // Caja cerrada del mismo día (se puede reabrir)
  const isClosedToday = isClosed && reg.opened_at && isTodayDate(reg.opened_at);

  const cardClass  = isPastDay ? 'caj-card-pastday'
    : isOpen   ? 'caj-card-open'
    : isClosed ? 'caj-card-closed'
    : 'caj-card-closed';

  return (
    <div className={`caj-card ${cardClass}`}>
      <div className="caj-card-top">
        <div className="caj-card-icon">
          {isPastDay  ? <FiAlertTriangle size={22} />
            : isOpen  ? <FiUnlock size={22} />
            : <FiLock size={22} />}
        </div>
        <div className="caj-card-info">
          <span className="caj-card-name">{caja?.name || reg?.caja_name || reg?.warehouse_name}</span>
          {(caja?.warehouse_name || reg?.warehouse_name) && (
            <span className="caj-card-subname">{caja?.warehouse_name || reg?.warehouse_name}</span>
          )}
          <span className={`caj-badge-estado ${
            isPastDay ? 'caj-badge-pastday'
              : isOpen ? 'caj-badge-open'
              : 'caj-badge-closed'
          }`}>
            {isPastDay ? <><FiAlertTriangle size={11} /> DÍA ANTERIOR</>
              : isOpen ? 'CAJA ABIERTA'
              : 'CAJA CERRADA'}
          </span>
        </div>
      </div>

      {reg && (
        <div className="caj-card-meta">
          <div className="caj-card-meta-row">
            <span>Apertura</span>
            <span className={isPastDay ? 'caj-text-warn' : ''}>{fmtDatetime(reg.opened_at)}</span>
          </div>
          {isClosed && (
            <div className="caj-card-meta-row">
              <span>Cierre</span>
              <span>{fmtDatetime(reg.closed_at)}</span>
            </div>
          )}
          <div className="caj-card-meta-row">
            <span>Monto apertura</span>
            <span className="caj-fw">S/ {fmt(reg.open_amount)}</span>
          </div>
          {isClosed && (
            <div className="caj-card-meta-row">
              <span>Monto cuadre</span>
              <span className="caj-fw">S/ {fmt(reg.close_amount)}</span>
            </div>
          )}
          {isClosed && (
            <div className="caj-card-meta-row">
              <span>Diferencia</span>
              <span className={parseFloat(reg.difference) < 0 ? 'caj-neg caj-fw' : parseFloat(reg.difference) > 0 ? 'caj-pos caj-fw' : 'caj-zero'}>
                S/ {fmt(reg.difference)}
              </span>
            </div>
          )}
          <div className="caj-card-meta-row">
            <span>Responsable</span>
            <span>{reg.username}</span>
          </div>
          {isPastDay && (
            <div className="caj-pastday-alert">
              <FiAlertTriangle size={13} />
              No se permiten operaciones en cajas de días anteriores.
            </div>
          )}
        </div>
      )}

      <div className="caj-card-actions">
        {isPastDay ? (
          <button className="caj-btn caj-btn-warning caj-btn-block" onClick={() => onAbrir(caja)}>
            <FiUnlock size={14} /> Cerrar anterior y Abrir nueva Caja
          </button>
        ) : isOpen ? (
          <button className="caj-btn caj-btn-primary caj-btn-block" onClick={() => onVer(caja, reg)}>
            <FiClock size={14} /> Ver / Gestionar Caja
          </button>
        ) : isClosed ? (
          <>
            <button className="caj-btn caj-btn-secondary caj-btn-block" onClick={() => onVer(null, reg)}>
              <FiFileText size={14} /> Ver Resumen y Movimientos
            </button>
            {/* Issue 2: caja cerrada del mismo día → reabrir (no crear nueva) */}
            {isClosedToday && onReabrir ? (
              <button className="caj-btn caj-btn-primary caj-btn-block" style={{ marginTop: 6 }}
                onClick={() => onReabrir(reg)}>
                <FiUnlock size={14} /> Reabrir Caja (continuar hoy)
              </button>
            ) : (!isClosedToday && !isClosedView && onAbrir && caja) ? (
              <button className="caj-btn caj-btn-success caj-btn-block" style={{ marginTop: 6 }}
                onClick={() => onAbrir(caja)}>
                <FiUnlock size={14} /> Abrir Nueva Caja
              </button>
            ) : null}
          </>
        ) : (
          <button className="caj-btn caj-btn-success caj-btn-block" onClick={() => onAbrir(caja)}>
            <FiUnlock size={14} /> Abrir Caja
          </button>
        )}
      </div>
    </div>
  );
}

// ── Panel admin: cajas abiertas ahora mismo de TODOS los usuarios ────────────
function PanelCajasTodos({ regs }) {
  if (!regs.length) return null;
  return (
    <div className="caj-admin-panel">
      <div className="caj-section-sep">
        <span>
          <FiUnlock size={14} /> CAJAS ABIERTAS — TODOS LOS USUARIOS
          <span className="caj-badge-count caj-badge-open" style={{ marginLeft: 8 }}>{regs.length}</span>
        </span>
      </div>
      <table className="caj-table caj-table-compact" style={{ margin: '0 20px 16px', width: 'calc(100% - 40px)' }}>
        <thead>
          <tr>
            <th>Almacén</th>
            <th>Usuario</th>
            <th>Apertura</th>
            <th className="caj-right">Monto Apertura</th>
          </tr>
        </thead>
        <tbody>
          {regs.map(r => (
            <tr key={r.id} className={r.is_past_day ? 'caj-text-warn' : ''}>
              <td className="caj-fw">{r.warehouse_name}</td>
              <td>{r.full_name || r.username}</td>
              <td>{fmtDatetime(r.opened_at)}</td>
              <td className="caj-right">S/ {fmt(r.open_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Panel admin: administrar Cajas (crear / editar / activar-desactivar) ─────
function PanelAdminCajas({ cajas, onNueva, onEditar, onToggle }) {
  return (
    <div className="caj-admin-panel">
      <div className="caj-section-sep">
        <span>
          <FiSettings size={14} /> ADMINISTRAR CAJAS
          <span className="caj-badge-count caj-badge-open" style={{ marginLeft: 8 }}>{cajas.length}</span>
        </span>
        <button className="caj-btn caj-btn-primary caj-btn-sm" onClick={onNueva}>
          <FiPlus size={13} /> Nueva Caja
        </button>
      </div>
      {cajas.length === 0 ? (
        <div className="caj-empty-inline" style={{ margin: '0 20px 16px' }}>
          Aún no has creado ninguna caja. Usa "Nueva Caja" para crear la primera.
        </div>
      ) : (
        <table className="caj-table caj-table-compact" style={{ margin: '0 20px 16px', width: 'calc(100% - 40px)' }}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Almacén</th>
              <th>Usuario Asignado</th>
              <th>Estado</th>
              <th className="caj-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cajas.map(c => (
              <tr key={c.id} className={!c.active ? 'caj-text-warn' : ''}>
                <td className="caj-fw">{c.name}</td>
                <td>{c.warehouse_name}</td>
                <td>{c.assigned_full_name || c.assigned_username || <span className="caj-muted">Sin asignar</span>}</td>
                <td>
                  <span className={`caj-badge-estado ${c.active ? 'caj-badge-open' : 'caj-badge-closed'}`}>
                    {c.active ? 'ACTIVA' : 'INACTIVA'}
                  </span>
                </td>
                <td className="caj-right">
                  <div className="caj-ia-actions">
                    <button className="caj-ia-btn caj-ia-btn-edit" onClick={() => onEditar(c)} title="Editar">
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      className={`caj-ia-btn ${c.active ? 'caj-ia-btn-off' : 'caj-ia-btn-on'}`}
                      onClick={() => onToggle(c)}
                      title={c.active ? 'Desactivar' : 'Activar'}
                    >
                      {c.active ? <FiXCircle size={14} /> : <FiCheckCircle size={14} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Vista: Lista de cajas con filtros ──────────────────────────────────────────
function ListaCajas({ cajas, myRegisters, closedRegisters, allOpenRegs, allCajasAdmin, isAdmin,
                      onVerCaja, onAbrirCaja, onReabrirCaja,
                      onNuevaCaja, onEditarCaja, onToggleCaja,
                      onRefresh, loading, filterFrom, filterTo, onFilterChange, isToday }) {
  const totalAbiertas = cajas.filter(c => myRegisters[c.id] && myRegisters[c.id].status === 'open').length;

  return (
    <div className="caj-lista-view">
      {/* ── Filtros de fecha ── */}
      <div className="caj-filter-bar">
        <span className="caj-filter-label"><FiClock size={14} /> Período</span>
        <input
          type="date" className="caj-filter-input"
          value={filterFrom}
          onChange={e => onFilterChange('from', e.target.value)}
        />
        <span className="caj-filter-sep">–</span>
        <input
          type="date" className="caj-filter-input"
          value={filterTo}
          onChange={e => onFilterChange('to', e.target.value)}
        />
        <button className="caj-btn caj-btn-primary caj-btn-sm" onClick={onRefresh} disabled={loading}>
          <FiRefreshCw size={13} className={loading ? 'caj-spin' : ''} /> Buscar
        </button>
        <button
          className="caj-btn caj-btn-secondary caj-btn-sm"
          onClick={() => onFilterChange('today', null)}
        >
          Hoy
        </button>
      </div>

      {/* ── Sección administrar cajas (solo admin, cuando vemos hoy) ── */}
      {isToday && isAdmin && (
        <PanelAdminCajas cajas={allCajasAdmin} onNueva={onNuevaCaja} onEditar={onEditarCaja} onToggle={onToggleCaja} />
      )}

      {/* ── Sección cajas activas (solo cuando vemos hoy) ── */}
      {isToday && (
        <>
          <div className="caj-section-sep">
            <span>
              <FiUnlock size={14} /> MIS CAJAS
              <span className="caj-badge-count caj-badge-open" style={{ marginLeft: 8 }}>
                {totalAbiertas} abierta{totalAbiertas !== 1 ? 's' : ''}
              </span>
            </span>
          </div>
          {cajas.length === 0 ? (
            <div className="caj-empty-movs" style={{ marginTop: 8, marginBottom: 24 }}>
              <FiLock size={40} />
              <p>No tienes ninguna caja asignada. Contacta al administrador.</p>
            </div>
          ) : (
            <div className="caj-cards-grid">
              {cajas.map(caja => {
                const reg = myRegisters[caja.id] || null;
                return (
                  <CajaCard
                    key={caja.id}
                    reg={reg}
                    caja={caja}
                    onVer={onVerCaja}
                    onReabrir={onReabrirCaja}
                    onAbrir={onAbrirCaja}
                  />
                );
              })}
            </div>
          )}

          {isAdmin && <PanelCajasTodos regs={allOpenRegs} />}
        </>
      )}

      {/* ── Sección cierres del período ── */}
      {closedRegisters.length > 0 && (
        <>
          <div className="caj-section-sep">
            <span>
              <FiLock size={14} /> CIERRES DEL PERÍODO
              <span className="caj-badge-count caj-badge-closed" style={{ marginLeft: 8 }}>
                {closedRegisters.length} cierre{closedRegisters.length !== 1 ? 's' : ''}
              </span>
            </span>
          </div>
          <div className="caj-cards-grid">
            {closedRegisters.map(reg => (
              <CajaCard
                key={reg.id}
                reg={reg}
                caja={null}
                onVer={onVerCaja}
                onAbrir={null}
                isClosedView
              />
            ))}
          </div>
        </>
      )}

      {!loading && !isToday && closedRegisters.length === 0 && (
        <div className="caj-empty-movs" style={{ marginTop: 40 }}>
          <FiLock size={40} />
          <p>No hay cierres de caja en el período seleccionado</p>
        </div>
      )}
    </div>
  );
}

// ── Issue 3: Exportar a Excel (CSV) ──────────────────────────────────────────
function exportarExcelCaja(summary, register) {
  const { ventas, ventas_total, cobros, cobros_total, ventas_por_producto = [], movimientos, diferencia } = summary;
  const reg = summary.register || register;
  const rows = [];

  rows.push(['REPORTE DE CAJA — ' + (reg.warehouse_name || ''), '', '', '']);
  rows.push(['Responsable', reg.full_name || reg.username, 'Fecha Apertura', new Date(reg.opened_at).toLocaleString('es-PE', { timeZone: 'America/Lima' })]);
  if (reg.closed_at) rows.push(['Fecha Cierre', new Date(reg.closed_at).toLocaleString('es-PE', { timeZone: 'America/Lima' }), '', '']);
  rows.push(['']);

  // Ventas
  rows.push(['VENTAS POR FORMA DE PAGO', '', '', '']);
  rows.push(['Forma de Pago', 'Monto (S/)']);
  ventas.forEach(v => rows.push([v.name, parseFloat(v.total).toFixed(2)]));
  rows.push(['TOTAL VENTAS', parseFloat(ventas_total).toFixed(2)]);
  rows.push(['']);

  // Cobros
  if (cobros.length) {
    rows.push(['COBRO DE CRÉDITOS', '', '', '']);
    cobros.forEach(c => rows.push([c.name, parseFloat(c.total).toFixed(2)]));
    rows.push(['TOTAL COBRADO', parseFloat(cobros_total).toFixed(2)]);
    rows.push(['']);
  }

  // Ventas por producto
  if (ventas_por_producto.length) {
    rows.push(['VENTAS POR PRODUCTO', '', '', '']);
    rows.push(['Producto', 'Cantidad', 'Unidades', 'Total (S/)']);
    ventas_por_producto.forEach(p => rows.push([
      p.product_name,
      `${parseFloat(p.total_qty).toFixed(3)} ${p.unit_measure || ''}`,
      p.special_condition ? `${p.total_unidades} UND` : '',
      parseFloat(p.total_soles).toFixed(2),
    ]));
    rows.push(['TOTAL VENDIDO', '', '', ventas_por_producto.reduce((s, p) => s + p.total_soles, 0).toFixed(2)]);
    rows.push(['']);
  }

  // Movimientos en caja
  rows.push(['MOVIMIENTOS EN CAJA', '', '', '']);
  rows.push(['Monto Apertura', parseFloat(movimientos.open_amount).toFixed(2)]);
  rows.push(['Ventas en Efectivo', parseFloat(movimientos.ventas_efectivo).toFixed(2)]);
  rows.push(['Cobro Créditos Efectivo', parseFloat(movimientos.cobro_efectivo).toFixed(2)]);
  rows.push(['Ingreso Efectivo', parseFloat(movimientos.ingreso_efectivo).toFixed(2)]);
  rows.push(['Salida Efectivo', parseFloat(movimientos.salida_efectivo).toFixed(2)]);
  rows.push(['TOTAL EFECTIVO', parseFloat(movimientos.total_efectivo).toFixed(2)]);
  rows.push(['TOTAL INGRESOS', parseFloat(movimientos.total_ingresos).toFixed(2)]);
  rows.push(['']);
  if (reg.close_amount != null) rows.push(['Monto Cuadre', parseFloat(reg.close_amount || 0).toFixed(2)]);
  rows.push(['Diferencia', parseFloat(diferencia || 0).toFixed(2)]);

  const csv = '﻿' + rows.map(r => r.map(cell =>
    `"${String(cell ?? '').replace(/"/g, '""')}"`
  ).join(',')).join('\r\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `Caja_${reg.warehouse_name}_${(reg.opened_at || '').slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Issue 3: Imprimir / PDF ───────────────────────────────────────────────────
export function imprimirCaja(summary, register) {
  const { ventas, ventas_total, cobros, cobros_total, ventas_por_producto = [], movimientos, diferencia } = summary;
  const reg = summary.register || register;
  const fmt2 = n => parseFloat(n || 0).toFixed(2);
  const fdt  = ts => ts ? new Date(ts).toLocaleString('es-PE', { timeZone: 'America/Lima' }) : '—';

  const ventasHtml = ventas.map(v =>
    `<tr><td>${v.name}</td><td class="r">${fmt2(v.total)}</td></tr>`
  ).join('') + `<tr class="tot"><td>TOTAL VENTAS</td><td class="r">${fmt2(ventas_total)}</td></tr>`;

  const cobrosHtml = cobros.length
    ? cobros.map(c => `<tr><td>${c.name}</td><td class="r">${fmt2(c.total)}</td></tr>`).join('')
      + `<tr class="tot"><td>TOTAL COBRADO</td><td class="r">${fmt2(cobros_total)}</td></tr>`
    : '<tr><td colspan="2" style="color:#999">Sin cobros</td></tr>';

  const productosHtml = ventas_por_producto.length
    ? ventas_por_producto.map(p =>
        `<tr><td>${p.product_name}</td><td class="r">${fmt2(p.total_qty)} ${p.unit_measure || ''}</td><td class="r">${p.special_condition ? `${p.total_unidades} UND` : '—'}</td><td class="r">${fmt2(p.total_soles)}</td></tr>`
      ).join('') + `<tr class="tot"><td colspan="3">TOTAL VENDIDO</td><td class="r">${fmt2(ventas_por_producto.reduce((s, p) => s + p.total_soles, 0))}</td></tr>`
    : '';

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Reporte de Caja</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; color: #111; }
      h1 { font-size: 16px; color: #0d1b2e; margin-bottom: 4px; }
      h2 { font-size: 13px; color: #0369a1; margin: 16px 0 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 3px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
      th { background: #c8ddf0; padding: 5px 8px; text-align: left; }
      td { padding: 4px 8px; border-bottom: 1px solid #f1f5f9; }
      .r { text-align: right; }
      .tot td { font-weight: bold; background: #f0f9ff; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; margin-bottom: 12px; }
      .info-row { display: flex; gap: 8px; }
      .label { color: #64748b; min-width: 120px; }
      .dif-neg { color: #dc2626; font-weight: bold; }
      .dif-pos { color: #059669; font-weight: bold; }
      @media print { button { display: none; } }
    </style>
  </head><body>
    <h1>REPORTE DE CAJA — ${reg.warehouse_name || ''}</h1>
    <div class="info-grid">
      <div class="info-row"><span class="label">Responsable:</span> <b>${reg.full_name || reg.username}</b></div>
      <div class="info-row"><span class="label">Estado:</span> <b>${reg.status === 'open' ? 'ABIERTA' : 'CERRADA'}</b></div>
      <div class="info-row"><span class="label">Fecha Apertura:</span> ${fdt(reg.opened_at)}</div>
      ${reg.closed_at ? `<div class="info-row"><span class="label">Fecha Cierre:</span> ${fdt(reg.closed_at)}</div>` : ''}
      <div class="info-row"><span class="label">Monto Apertura:</span> S/ ${fmt2(reg.open_amount)}</div>
      ${reg.close_amount != null ? `<div class="info-row"><span class="label">Monto Cuadre:</span> S/ ${fmt2(reg.close_amount)}</div>` : ''}
      <div class="info-row"><span class="label">Diferencia:</span>
        <span class="${parseFloat(diferencia) < 0 ? 'dif-neg' : parseFloat(diferencia) > 0 ? 'dif-pos' : ''}">
          S/ ${fmt2(diferencia)}
        </span>
      </div>
    </div>

    <h2>Ventas por Forma de Pago</h2>
    <table><tr><th>Forma de Pago</th><th style="text-align:right">Monto (S/)</th></tr>${ventasHtml}</table>

    <h2>Cobro de Créditos</h2>
    <table><tr><th>Forma de Pago</th><th style="text-align:right">Monto (S/)</th></tr>${cobrosHtml}</table>

    ${productosHtml ? `
    <h2>Ventas por Producto</h2>
    <table><tr><th>Producto</th><th style="text-align:right">Cantidad</th><th style="text-align:right">Unidades</th><th style="text-align:right">Total (S/)</th></tr>${productosHtml}</table>
    ` : ''}

    <h2>Movimientos en Caja</h2>
    <table>
      <tr><td>Monto Apertura</td><td class="r">S/ ${fmt2(movimientos.open_amount)}</td></tr>
      <tr><td>Ventas en Efectivo</td><td class="r">S/ ${fmt2(movimientos.ventas_efectivo)}</td></tr>
      <tr><td>Cobro Créditos Efectivo</td><td class="r">S/ ${fmt2(movimientos.cobro_efectivo)}</td></tr>
      <tr><td>Ingreso Efectivo</td><td class="r">S/ ${fmt2(movimientos.ingreso_efectivo)}</td></tr>
      <tr><td>Salida Efectivo</td><td class="r">S/ ${fmt2(movimientos.salida_efectivo)}</td></tr>
      <tr class="tot"><td>TOTAL EFECTIVO</td><td class="r">S/ ${fmt2(movimientos.total_efectivo)}</td></tr>
      <tr class="tot"><td>TOTAL INGRESOS</td><td class="r">S/ ${fmt2(movimientos.total_ingresos)}</td></tr>
    </table>
    <script>window.onload = () => { window.print(); }</script>
  </body></html>`;

  const win = window.open('', '_blank', 'width=800,height=700');
  win.document.write(html);
  win.document.close();
}

// ── Componente principal ──────────────────────────────────────────────────────
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AperturaCajas() {
  const { user } = useAuth();
  const businessId = user?.business_id;
  const isAdmin = user?.role === 'admin';
  const [allCajas, setAllCajas]                = useState([]);   // respuesta cruda: admin=todas, no-admin=solo asignadas activas
  const [warehousesList, setWarehousesList]    = useState([]);   // admin: para el select de ModalCaja
  const [usersList, setUsersList]              = useState([]);   // admin: cajeros disponibles para asignar
  const [myRegisters, setMyRegisters]         = useState({});   // por caja.id, SOLO del usuario logueado
  const [allOpenRegs, setAllOpenRegs]         = useState([]);   // admin: cajas abiertas de todos los usuarios
  const [closedRegisters, setClosedRegisters] = useState([]);   // Issue 1+3
  const [loading, setLoading]                 = useState(true);
  const [actionLoading, setActionLoading]     = useState(false);

  // Filtro de fecha
  const [filterFrom, setFilterFrom] = useState(todayStr);
  const [filterTo, setFilterTo]     = useState(todayStr);

  // Vista: 'lista' | 'detalle'
  const [view, setView]                       = useState('lista');
  const [selectedCaja, setSelectedCaja]       = useState(null);
  const [register, setRegister]               = useState(null);
  const [readOnly, setReadOnly]               = useState(false);   // Issue 2
  const [summary, setSummary]                 = useState(null);

  const [showCuadre, setShowCuadre]           = useState(false);
  const [showAbrirModal, setShowAbrirModal]   = useState(false);
  const [abrirTargetCaja, setAbrirTargetCaja] = useState(null);
  const [showCajaModal, setShowCajaModal]     = useState(null);   // null | {mode:'create'} | {mode:'edit', caja}
  const [savingCaja, setSavingCaja]           = useState(false);

  const [toast, setToast] = useState({ msg: '', type: 'ok' });
  const [error, setError] = useState('');

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'ok' }), 3500);
  };

  const isToday = filterFrom === todayStr() && filterTo === todayStr();

  // ── Carga Cajas asignadas + mi última sesión en cada una + cierres del período ──
  const loadLista = useCallback(async (from, to) => {
    setLoading(true);
    setError('');
    const fFrom = from || filterFrom;
    const fTo   = to   || filterTo;
    try {
      const cdRes = await getCashDesks();
      const raw = cdRes.data.data || [];
      setAllCajas(raw);
      const cajasActivas = raw.filter(c => c.active);

      // Cargar MI última sesión (abierta o cerrada del día) en cada Caja asignada
      const map = {};
      await Promise.all(cajasActivas.map(async (c) => {
        try {
          const res = await getCashRegisterLatest(c.id);
          map[c.id] = res.data.data || null;
        } catch { map[c.id] = null; }
      }));
      setMyRegisters(map);

      // Admin: almacenes + usuarios (para crear/editar Cajas) y cajas abiertas de todos
      if (isAdmin) {
        try {
          const whRes = await getWarehouses(businessId);
          setWarehousesList((whRes.data.data || []).filter(w => !!w.active));
        } catch { setWarehousesList([]); }
        try {
          const uRes = await api.get('/api/users');
          setUsersList((uRes.data.data || []).filter(u => u.role !== 'admin' && u.active));
        } catch { setUsersList([]); }
        try {
          const allRes = await getAllOpenCashRegisters();
          setAllOpenRegs(allRes.data.data || []);
        } catch { setAllOpenRegs([]); }
      }

      // Filtro de fechas: cargar cierres del período para sección histórica
      const histRes = await getCashRegisterHistory({ from_date: fFrom, to_date: fTo, limit: 200 });
      // En "hoy": los cierres de hoy ya aparecen en las cards de Caja, no duplicar
      // En período pasado: mostrar todos los cierres del rango
      const isTodayRange = fFrom === todayStr() && fTo === todayStr();
      const closed = (histRes.data.rows || []).filter(r =>
        r.status === 'closed' && (!isTodayRange)
      );
      setClosedRegisters(closed);
    } catch {
      setError('Error al cargar datos de caja');
    } finally {
      setLoading(false);
    }
  }, [filterFrom, filterTo, businessId, isAdmin]);

  useEffect(() => { loadLista(); }, []); // eslint-disable-line

  const handleFilterChange = (type, value) => {
    let from = filterFrom, to = filterTo;
    if (type === 'from')  { from = value; setFilterFrom(value); }
    if (type === 'to')    { to   = value; setFilterTo(value);   }
    if (type === 'today') { from = todayStr(); to = todayStr(); setFilterFrom(from); setFilterTo(to); }
    loadLista(from, to);
  };

  // ── Entrar al detalle de una caja ABIERTA ─────────────────────────────────
  const handleVerCaja = async (caja, reg) => {
    setSelectedCaja(caja);
    setRegister(reg);
    setReadOnly(false);
    try {
      const sumRes = await getCashRegisterSummary(reg.id);
      setSummary(sumRes.data.data);
    } catch { setSummary(null); }
    setView('detalle');
  };

  // ── Abrir modal de apertura ───────────────────────────────────────────────
  const handleAbrirClick = (caja) => {
    setAbrirTargetCaja(caja);
    setShowAbrirModal(true);
  };

  // ── Confirmar apertura de caja (solo Soles) ──────────────────────────────
  const handleConfirmAbrir = async (cajaId, openAmount) => {
    setActionLoading(true);
    try {
      await openCashRegister({ caja_id: cajaId, open_amount: openAmount });

      setShowAbrirModal(false);
      showToast('Caja abierta correctamente');

      const regRes = await getCashRegisterCurrent(cajaId);
      const reg = regRes.data.data;
      if (reg) {
        const sumRes = await getCashRegisterSummary(reg.id);
        setRegister(reg);
        setSummary(sumRes.data.data);
        setSelectedCaja(abrirTargetCaja);
        setView('detalle');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al abrir caja', 'err');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Issue 2: Reabrir una caja cerrada del día actual ─────────────────────
  const handleReabrir = async (reg) => {
    setActionLoading(true);
    try {
      await reopenCashRegister(reg.id);
      showToast('Caja reabierta correctamente');
      // Recargar y entrar al detalle de la caja reabierta
      const sumRes = await getCashRegisterSummary(reg.id);
      setRegister({ ...reg, status: 'open', close_amount: null, difference: null, closed_at: null });
      setSummary(sumRes.data.data);
      setSelectedCaja({ name: reg.caja_name || reg.warehouse_name });
      setReadOnly(false);
      setView('detalle');
      loadLista();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al reabrir caja', 'err');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Administrar Cajas (solo admin): crear / editar / activar-desactivar ──
  const handleNuevaCaja = () => setShowCajaModal({ mode: 'create' });
  const handleEditarCaja = (caja) => setShowCajaModal({ mode: 'edit', caja });

  const handleSaveCaja = async (payload) => {
    setSavingCaja(true);
    try {
      if (showCajaModal.mode === 'create') {
        await createCashDesk(payload);
        showToast('Caja creada correctamente');
      } else {
        await updateCashDesk(showCajaModal.caja.id, payload);
        showToast('Caja actualizada correctamente');
      }
      setShowCajaModal(null);
      await loadLista();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al guardar la caja', 'err');
    } finally {
      setSavingCaja(false);
    }
  };

  const handleToggleCajaActive = async (caja) => {
    try {
      await toggleCashDeskActive(caja.id);
      showToast(caja.active ? 'Caja desactivada' : 'Caja activada');
      await loadLista();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al cambiar estado de la caja', 'err');
    }
  };

  // ── Issue 1+2: Ver detalle de una caja CERRADA (solo lectura) ───────────
  const handleVerCierre = async (reg) => {
    setSelectedCaja(null);
    setRegister(reg);
    setReadOnly(true);          // Issue 2: modo lectura
    try {
      const sumRes = await getCashRegisterSummary(reg.id);
      setSummary(sumRes.data.data);
    } catch { setSummary(null); }
    setView('detalle');
  };

  // ── Volver a la lista ─────────────────────────────────────────────────────
  const handleVolverLista = async () => {
    setView('lista');
    setSelectedCaja(null);
    setRegister(null);
    setSummary(null);
    setReadOnly(false);
    await loadLista();
  };

  // ── Refrescar detalle ─────────────────────────────────────────────────────
  const handleRefreshDetalle = async () => {
    if (!register) return;
    try {
      const sumRes = await getCashRegisterSummary(register.id);
      setSummary(sumRes.data.data);
      showToast('Resumen actualizado');
    } catch {
      showToast('Error al actualizar', 'err');
    }
  };

  // ── Cerrar caja ───────────────────────────────────────────────────────────
  const handleCerrar = () => setShowCuadre(true);

  const handleConfirmCierre = async (closeAmount) => {
    setActionLoading(true);
    try {
      const result = await closeCashRegister(register.id, { close_amount: closeAmount });
      setShowCuadre(false);
      showToast('Caja cerrada correctamente');
      // Issue 1: en vez de volver a la lista, mostrar el resumen del cierre
      const closedReg = { ...register, status: 'closed', close_amount: closeAmount,
        difference: result.data?.data?.difference ?? 0, closed_at: new Date().toISOString() };
      setRegister(closedReg);
      setReadOnly(true);
      const sumRes = await getCashRegisterSummary(register.id);
      setSummary(sumRes.data.data);
      // Refrescar lista en background
      loadLista();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al cerrar caja', 'err');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Render: loading / error / sin almacenes ───────────────────────────────
  if (loading && view === 'lista') {
    return (
      <div className="caj-page">
        <div className="caj-loading caj-loading-full">
          <div className="caj-spinner" />
          Cargando módulo de caja...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="caj-page">
        <div className="caj-error-full">
          <FiAlertTriangle size={36} />
          <p>{error}</p>
          <button className="caj-btn caj-btn-primary" onClick={loadLista}>Reintentar</button>
        </div>
      </div>
    );
  }

  // ── Render: vista lista ───────────────────────────────────────────────────
  if (view === 'lista') {
    const cajasActivas = allCajas.filter(c => c.active);
    return (
      <div className="caj-page">
        <ListaCajas
          cajas={cajasActivas}
          allCajasAdmin={allCajas}
          myRegisters={myRegisters}
          closedRegisters={closedRegisters}
          allOpenRegs={allOpenRegs}
          isAdmin={isAdmin}
          onVerCaja={(caja, reg) => reg?.status === 'closed' ? handleVerCierre(reg) : handleVerCaja(caja, reg)}
          onAbrirCaja={handleAbrirClick}
          onReabrirCaja={handleReabrir}
          onNuevaCaja={handleNuevaCaja}
          onEditarCaja={handleEditarCaja}
          onToggleCaja={handleToggleCajaActive}
          onRefresh={() => loadLista()}
          loading={loading}
          filterFrom={filterFrom}
          filterTo={filterTo}
          onFilterChange={handleFilterChange}
          isToday={isToday}
        />

        {showAbrirModal && abrirTargetCaja && (
          <ModalAbrirCaja
            caja={abrirTargetCaja}
            onOpen={handleConfirmAbrir}
            onCancel={() => !actionLoading && setShowAbrirModal(false)}
            loading={actionLoading}
          />
        )}

        {showCajaModal && (
          <ModalCaja
            mode={showCajaModal.mode}
            initial={showCajaModal.caja}
            warehouses={warehousesList}
            users={usersList}
            onSave={handleSaveCaja}
            onCancel={() => !savingCaja && setShowCajaModal(null)}
            loading={savingCaja}
          />
        )}

        {toast.msg && (
          <div className={`caj-toast ${toast.type === 'err' ? 'caj-toast-err' : ''}`}>
            {toast.type === 'err' ? <FiAlertTriangle size={16} /> : <FiCheckCircle size={16} />}
            {toast.msg}
          </div>
        )}
      </div>
    );
  }

  // ── Render: vista detalle ─────────────────────────────────────────────────
  const isClosed   = register?.status === 'closed';
  const isPastDay2 = !isClosed && (register?.is_past_day || !isTodayDate(register?.opened_at));
  const blockOps   = readOnly || isClosed || isPastDay2;  // Issue 2+R3

  return (
    <div className="caj-page">
      <div className="caj-topbar">
        <div className="caj-topbar-left">
          <button className="caj-btn caj-btn-secondary caj-btn-sm" onClick={handleVolverLista}>
            <FiArrowLeft size={15} /> Volver a Cajas
          </button>
          <div className="caj-warehouse-tag">
            <FiDollarSign size={14} />
            <strong>{selectedCaja?.name || register?.caja_name || register?.warehouse_name}</strong>
          </div>
          {/* Issue 1: badge cuando se acaba de cerrar o es solo lectura */}
          {readOnly && (
            <span className="caj-badge-estado caj-badge-closed" style={{ marginLeft: 8 }}>
              <FiLock size={12} /> CIERRE — SOLO LECTURA
            </span>
          )}
        </div>
        <div className="caj-topbar-right">
          {!readOnly && (
            <button className="caj-btn-icon" onClick={handleRefreshDetalle} title="Actualizar resumen">
              <FiRefreshCw size={15} />
            </button>
          )}
          {/* Issue 3: exportar cuando es modo lectura (caja cerrada) */}
          {(readOnly || isClosed) && summary && (
            <>
              <button className="caj-btn caj-btn-secondary caj-btn-sm"
                onClick={() => exportarExcelCaja(summary, register)}
                title="Exportar a Excel">
                <FiFileText size={14} /> Excel
              </button>
              <button className="caj-btn caj-btn-secondary caj-btn-sm"
                onClick={() => imprimirCaja(summary, register)}
                title="Imprimir / PDF">
                <FiPrinter size={14} /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      <div className="caj-register-view">
        <div className="caj-tab-content">
          <TabInicio
            summary={summary}
            onCerrar={handleCerrar}
            onBalanceClick={() => setShowCuadre(true)}
            readOnly={readOnly}  // Issue 2: oculta botones de acción
            registerId={register.id}
            isPastDay={blockOps}  // Issue 2+R3
          />
        </div>
      </div>

      {toast.msg && (
        <div className={`caj-toast ${toast.type === 'err' ? 'caj-toast-err' : ''}`}>
          {toast.type === 'err' ? <FiAlertTriangle size={16} /> : <FiCheckCircle size={16} />}
          {toast.msg}
        </div>
      )}

      {showCuadre && summary && (
        <ModalCuadre
          totalEfectivo={summary.movimientos.total_efectivo}
          onConfirm={handleConfirmCierre}
          onCancel={() => !actionLoading && setShowCuadre(false)}
        />
      )}
    </div>
  );
}
